import * as React from 'react';
import {
    Add as AddIcon,
    LocalShipping as TruckIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
} from '@mui/material';
import { Redirect } from 'react-router';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from '../../helpers/axios';
import OperationsHeader from './OperationsHeader';

export interface IProps {
    user: {
        id: number;
        name: string;
        email: string;
        role: string;
        is_active: boolean;
        selectedHierarchies: any[];
        selectedHierarchyIds: any[];
    };
}

interface VehicleFormData {
    vehicleNo: string;
    driverName: string;
    driverPhone: string;
    transporterName: string;
    transporterPhone: string;
}

interface Vehicle {
    id: number;
    hierarchy_id: number | string;
    vehicle_no: string;
    driver_name: string;
    driver_phone: string;
    transporter_name?: string | null;
    transporter_phone?: string | null;
}

const initialFormData: VehicleFormData = {
    vehicleNo: '',
    driverName: '',
    driverPhone: '',
    transporterName: '',
    transporterPhone: '',
};

const VehicleManagement: React.FC<IProps> = ({ user }) => {
    const [formData, setFormData] = React.useState<VehicleFormData>(initialFormData);
    const [vehicles, setVehicles] = React.useState<Vehicle[]>([]);
    const [selectedCity, setSelectedCity] = React.useState<any | null>(null);
    const [showAddModal, setShowAddModal] = React.useState(false);
    const [showEditModal, setShowEditModal] = React.useState(false);
    const [editData, setEditData] = React.useState<Vehicle | null>(null);
    const [deleteId, setDeleteId] = React.useState<number | null>(null);

    const hierarchyIds = user.selectedHierarchyIds || [];
    const hierarchyIdsKey = hierarchyIds.join(',');

    const fetchVehicles = React.useCallback(async () => {
        if (hierarchyIds.length === 0) {
            setVehicles([]);
            return;
        }

        try {
            const response = await axios.get('listVehicles', {
                params: { hierarchy_ids: hierarchyIds },
            });

            if (response.data.success === true) {
                setVehicles(response.data.data || []);
            } else {
                toast.error(response.data.msg || 'Failed to load vehicles');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.msg || 'Error loading vehicles');
        }
    // hierarchyIdsKey tracks the values without re-fetching for an unchanged array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hierarchyIdsKey]);

    React.useEffect(() => {
        fetchVehicles();
    }, [fetchVehicles]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [event.target.name]: event.target.value });
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        setSelectedCity(null);
    };

    const handleAddVehicle = async () => {
        if (!formData.vehicleNo || !formData.driverName || !formData.driverPhone || !selectedCity) {
            toast.error('Please fill all required fields (*)');
            return;
        }

        try {
            const response = await axios.post('addVehicle', {
                hierarchy_ids: [selectedCity.id],
                vehicle_no: formData.vehicleNo,
                driver_name: formData.driverName,
                driver_phone: formData.driverPhone,
                transporter_name: formData.transporterName,
                transporter_phone: formData.transporterPhone,
            });

            if (response.data.success === true) {
                toast.success('Vehicle added successfully!');
                closeAddModal();
                setFormData(initialFormData);
                fetchVehicles();
            } else {
                toast.error(response.data.msg || 'Failed to add vehicle');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.msg || 'Server error');
        }
    };

    const handleOpenEdit = (vehicle: Vehicle) => {
        setEditData({ ...vehicle });
        setSelectedCity(
            user.selectedHierarchies.find(
                (hierarchy: any) => String(hierarchy.id) === String(vehicle.hierarchy_id)
            ) || null
        );
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditData(null);
        setSelectedCity(null);
    };

    const handleSaveEdit = async () => {
        if (!editData || !editData.vehicle_no || !editData.driver_name || !editData.driver_phone || !selectedCity) {
            toast.error('Please fill all required fields (*)');
            return;
        }

        try {
            const response = await axios.post('editVehicle', {
                hierarchy_ids: [selectedCity.id],
                vehicle_id: editData.id,
                vehicle_no: editData.vehicle_no,
                driver_name: editData.driver_name,
                driver_phone: editData.driver_phone,
                transporter_name: editData.transporter_name,
                transporter_phone: editData.transporter_phone,
            });

            if (response.data.success === true) {
                toast.success('Vehicle updated successfully');
                closeEditModal();
                fetchVehicles();
            } else {
                toast.error(response.data.msg || 'Failed to update vehicle');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.msg || 'Server error');
        }
    };

    const handleDeleteVehicle = async () => {
        if (deleteId === null) return;

        try {
            const response = await axios.post('deleteVehicle', {
                hierarchy_ids: hierarchyIds,
                vehicle_id: deleteId,
            });

            if (response.data.success === true) {
                toast.success('Vehicle deleted');
                setDeleteId(null);
                fetchVehicles();
            } else {
                toast.error(response.data.msg || 'Failed to delete');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.msg || 'Server error');
        }
    };

    if (!localStorage.getItem('token')) {
        return <Redirect to="/login" />;
    }

    const primaryGreen = '#204d36';

    return (
        <Box sx={{ p: 2 }}>
            <OperationsHeader />
            <ToastContainer />

            <Box
                sx={{
                    mt: 4,
                    mb: 4,
                    background: '#fff',
                    p: 3,
                    borderRadius: 2,
                    boxShadow: 3,
                    maxWidth: '1200px',
                    mx: 'auto',
                }}
            >
                <Box
                    sx={{
                        background: '#f8f9fa',
                        borderRadius: '14px',
                        p: 3,
                        mb: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                background: primaryGreen,
                                p: 1.4,
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <TruckIcon sx={{ color: '#fff', fontSize: 28 }} />
                        </Box>
                        <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#333', margin: 0 }}>
                            Vehicle Management
                        </h1>
                    </Box>

                    <Button
                        variant="contained"
                        onClick={() => {
                            setShowAddModal(true);
                            setSelectedCity(null);
                        }}
                        startIcon={<AddIcon />}
                        sx={{ background: primaryGreen, fontWeight: 600 }}
                    >
                        Add Vehicle
                    </Button>
                </Box>

                <Box
                    sx={{
                        background: 'white',
                        borderRadius: '12px',
                        overflowX: 'auto',
                        border: '1px solid #e0e0e0',
                    }}
                >
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                        <thead>
                            <tr style={{ background: '#f8f9fa' }}>
                                {[
                                    'City',
                                    'Vehicle No',
                                    'Driver Name',
                                    'Driver Phone',
                                    'Transporter Name',
                                    'Transporter Phone',
                                    'Actions',
                                ].map((header) => (
                                    <th
                                        key={header}
                                        style={{
                                            padding: '12px',
                                            textAlign: 'center',
                                            border: '1px solid #e0e0e0',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {vehicles.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ padding: '48px', textAlign: 'center' }}>
                                        <TruckIcon style={{ fontSize: 48, color: '#ccc' }} />
                                        <p>No vehicles added yet.</p>
                                    </td>
                                </tr>
                            ) : (
                                vehicles.map((vehicle) => (
                                    <tr key={vehicle.id}>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            {user.selectedHierarchies.find(
                                                (hierarchy: any) => String(hierarchy.id) === String(vehicle.hierarchy_id)
                                            )?.name || '-'}
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>{vehicle.vehicle_no}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>{vehicle.driver_name}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>{vehicle.driver_phone}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>{vehicle.transporter_name || '-'}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>{vehicle.transporter_phone || '-'}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <IconButton aria-label="Edit vehicle" onClick={() => handleOpenEdit(vehicle)} sx={{ color: 'blue' }}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton aria-label="Delete vehicle" onClick={() => setDeleteId(vehicle.id)} sx={{ color: 'red' }}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </Box>
            </Box>

            <Dialog open={showAddModal} onClose={closeAddModal} maxWidth="xs" fullWidth>
                <DialogTitle>Add Vehicle</DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Autocomplete
                        options={user.selectedHierarchies}
                        getOptionLabel={(option: any) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={selectedCity}
                        onChange={(_event, value) => setSelectedCity(value)}
                        renderInput={(params) => <TextField {...params} label="City *" margin="normal" />}
                    />
                    {[
                        { label: 'Vehicle No *', name: 'vehicleNo' },
                        { label: 'Driver Name *', name: 'driverName' },
                        { label: 'Driver Phone *', name: 'driverPhone' },
                        { label: 'Transporter Name', name: 'transporterName' },
                        { label: 'Transporter Phone', name: 'transporterPhone' },
                    ].map((field) => (
                        <TextField
                            key={field.name}
                            fullWidth
                            label={field.label}
                            name={field.name}
                            value={formData[field.name as keyof VehicleFormData]}
                            onChange={handleChange}
                            margin="normal"
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button className="customCloseButton" onClick={closeAddModal}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddVehicle}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={showEditModal} onClose={closeEditModal} maxWidth="xs" fullWidth>
                <DialogTitle>Edit Vehicle</DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Autocomplete
                        options={user.selectedHierarchies}
                        getOptionLabel={(option: any) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={selectedCity}
                        onChange={(_event, value) => setSelectedCity(value)}
                        renderInput={(params) => <TextField {...params} label="City *" margin="normal" />}
                    />
                    {editData && (
                        <>
                            <TextField fullWidth label="Vehicle No *" value={editData.vehicle_no} onChange={(event) => setEditData({ ...editData, vehicle_no: event.target.value })} margin="normal" />
                            <TextField fullWidth label="Driver Name *" value={editData.driver_name} onChange={(event) => setEditData({ ...editData, driver_name: event.target.value })} margin="normal" />
                            <TextField fullWidth label="Driver Phone *" value={editData.driver_phone} onChange={(event) => setEditData({ ...editData, driver_phone: event.target.value })} margin="normal" />
                            <TextField fullWidth label="Transporter Name" value={editData.transporter_name || ''} onChange={(event) => setEditData({ ...editData, transporter_name: event.target.value })} margin="normal" />
                            <TextField fullWidth label="Transporter Phone" value={editData.transporter_phone || ''} onChange={(event) => setEditData({ ...editData, transporter_phone: event.target.value })} margin="normal" />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button className="customCloseButton" onClick={closeEditModal}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveEdit}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete Vehicle</DialogTitle>
                <DialogContent>Are you sure you want to delete this vehicle?</DialogContent>
                <DialogActions>
                    <Button className="customCloseButton" onClick={() => setDeleteId(null)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteVehicle}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VehicleManagement;
